import { IUser } from '../utils/userHandler';
import { AddRaceDTO, AddVisitDTO, CountByStateResult, Opportunity, Planning, Race, RaceState, Visit, VisitState } from './model';
import ClientApi from './client-api';
import DriverApi from './driver-api';
import AdminApi from './admin-api';
import FCMApi from './fcm-api';
import CompanyApi from './company-api';
import BranchApi from './branch-api';
import ReportApi from './report-api';
import LocationApi from './location-api';

export class BaseCaller {

    private path: string;
    private token: string | null;
    private handler: (response: any) => boolean;

    constructor(path: string, token: string | null) {
        this.path = path;
        this.token = token;

        this.handleResponse = this.handleResponse.bind(this);
    }

    public setPath(path: string) {
        if (this.path === path) {
            return;
        }
        this.path = path;
        this.token = null;
    }

    public setToken(token: string | null) {
        this.token = token
    }

    public get(suffix: string): Promise<any> {
        console.info("api url: " + this.path + "/" + suffix);
        console.info("headers: ", this.buildHeaders());
        return fetch(`${this.path}/${suffix}`, {
            method: 'GET',
            headers: this.buildHeaders()
        }).then(this.handleResponse.bind(this));
    }

    public post(suffix: string, body: any): Promise<any> {

        return fetch(`${this.path}/${suffix}`, {
            method: 'POST',
            headers: this.buildHeaders(),
            body: JSON.stringify(body)
        }).then(this.handleResponse);
    }

    public put(suffix: string, body: any): Promise<any> {
        console.info('url: ' + this.path + '/' + suffix)
        console.info('body: ' + JSON.stringify(body))
        return fetch(`${this.path}/${suffix}`, {
            method: 'PUT',
            headers: this.buildHeaders(),
            body: JSON.stringify(body)
        }).then(this.handleResponse);
    }
    public delete(suffix: string): Promise<any> {
        return fetch(`${this.path}/${suffix}`, {
            method: 'DELETE',
            headers: this.buildHeaders()
        }).then(this.handleResponse);
    }

    public postForm(suffix: string, body: { [key: string]: string }) {
        const data = new URLSearchParams();
        for (const key of Object.keys(body)) {
            data.append(key, body[key]);
        }

        return fetch(`${this.path}/${suffix}`, {
            method: 'post',
            body: data,
        }).then(this.handleResponse)
    }

    public addHandler(handler: (err: any) => boolean) {
        this.handler = handler;
    }

    /**
     * Builds the common headers to use the API
     */
    private buildHeaders() {
        const headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: ''
        };

        if (this.token) {
            headers.Authorization = this.token;
        }
        return headers;
    }

    /**
     * Handle the server response.
     *
     * This check for the status code, and always try to return an JSON object.
     *
     * @param {Response} response
     */
    private handleResponse(response: any) {

        if (response.headers.get('Content-Type') === 'application/octet-stream') {
            return response.blob();
        }

        if (response.status >= 200 && response.status <= 300) {
            // 204 No Content no tiene cuerpo de respuesta
            if (response.status === 204) {
                return Promise.resolve({});
            }
            return response.json();
        }


        if (this.handler && this.handler(response)) {
            console.warn('without handler, returning ok when the result is not ok');
            return;
        }

        if (response.status === 401) {
            throw new Error(response.status);
        }

        return response.json().then((data: any) => {
            const error: any = new Error(response.statusText || response.status);
            error.status = response.status;
            error.statusText = response.statusText;
            error.response = data;
            return Promise.reject(error)
        })
    }

}

class VisitAPI {

    private caller: BaseCaller;

    constructor(caller: BaseCaller) {
        this.caller = caller;
    }

    public byId(id: string): Promise<any> {
        return this.caller.get(`visit/${id}`);
    }

    public all(page?: number, pageSize?: number, search: string = '', startDate?: string, endDate?: string): any {
        const params = new URLSearchParams();

        if (page !== undefined){ params.append('pageNumber', page.toString())};
        if (pageSize !== undefined){ params.append('pageSize', pageSize.toString())};
        if (search){ params.append('search', search)};
        if (startDate){ params.append('start_date', startDate)};
        if (endDate){ params.append('end_date', endDate)};

        const queryString = params.toString();
        const url = queryString ? `visit?${queryString}` : 'visit';

        return this.caller.get(url);
    }


    public add(data: AddVisitDTO): Promise<Opportunity> {
        return this.caller.post('admin/addOpportunity', data);
    };

    public listByState(state: VisitState): Promise<CountByStateResult[]> {
        return this.caller.get(`admin/opportunity/byState?state=${state}`);
    }

    public countByState(): Promise<CountByStateResult[]> {
        return this.caller.get('admin/opportunity/state');
    }

    public cancel(id: number): Promise<Opportunity> {
        return this.caller.post(`opportunity/${id}/cancel`, {});
    }

    public assign(id: number, driver: number): Promise<Opportunity> {
        return this.caller.post(`admin/opportunity/assignPermissive`, { visit: id, driver: driver });
    }

    public changeBranch(id: number, branch: number) {
        return this.caller.post('admin/opportunity/updateBranch', { visit: id, branch: branch })
    }

    public addVisit(data: any) {
        return this.caller.post('visit/schedule', data)
    }

    public visitById(id: string): Promise<Visit> {
        return this.caller.get(`visit/${id}`);
    }

}
class RaceAPI {

    private caller: BaseCaller;

    constructor(caller: BaseCaller) {
        this.caller = caller;
    }

    public byId(id: string): Promise<Race> {
        return this.caller.get(`opportunity/${id}`);
    }

    public all(page?: number, pageSize?: number, search: string = '', from?: string, to?: string,type?:string,driverId?:number,status?:string,sinCabecera:boolean = false,conOwner?:boolean,branchId?:number,branchDestinyId?:number,
        generalSearch?:string
    ): any {
        const params = new URLSearchParams();

        if (page !== undefined){ params.append('pageNumber', page.toString())};
        if (pageSize !== undefined){ params.append('pageSize', pageSize.toString())};
        if (search){ params.append('search', search)};
        if (from){ params.append('start_date', from)};
        if (to){ params.append('end_date', to)};
        if (driverId !== undefined){ params.append('driver_id', driverId.toString())};
        if (sinCabecera){ params.append('sin_cabecera', 'true')};
        if (status){ params.append('status', status)};
        if (conOwner){ params.append('con_owner', 'true')};
        if(type){ params.append('type', type)};
        if(branchId){ params.append('branch_id', branchId.toString())};
        if(branchDestinyId){ params.append('branch_destiny_id', branchDestinyId.toString())};
        if(generalSearch){ params.append('general', generalSearch)};

        const queryString = params.toString();
        const url = queryString ? `opportunity/admin?${queryString}` : 'opportunity/admin/';

        return this.caller.get(url);
    }

    public allPlaning(page?: number, pageSize?: number): any {
        const params = new URLSearchParams();
        if (page !== undefined){ params.append('pageNumber', page.toString())};
        if (pageSize !== undefined){ params.append('pageSize', pageSize.toString())}
        const queryString = params.toString();
        const url = queryString ? `planning/?${queryString}` : 'planning/';
        return this.caller.get(url);
    }

    public deletePlanning(id: number): Promise<any> {
        return this.caller.delete(`planning/${id}`);
    }


    public add(data: AddRaceDTO): Promise<Race> {
        return this.caller.post('admin/addOpportunity/schedule', data);
    };
    public addPlanificationMultiDay(data: Planning): Promise<Race> {
        return this.caller.post('planning/', data);
    }
    public update(data: any): Promise<Race> {
        return this.caller.put('opportunity/', data);
    };
    public updateState(data: any): Promise<Race> {
        const enviar = {
            raceId : data.id,
            state : data.state,
            finishedTime : data.finishedTime
        }
        return this.caller.put('opportunity/state', enviar);
    };

    public listByState(state: RaceState): Promise<CountByStateResult[]> {
        return this.caller.get(`admin/opportunity/byState?state=${state}`);
    }

    public countByState(): Promise<CountByStateResult[]> {
        return this.caller.get('admin/opportunity/state');
    }

    public cancel(id: number): Promise<Race> {
        return this.caller.post(`opportunity/${id}/cancel`, {});
    }

    public assign(id: number, driver: number): Promise<Race> {
        return this.caller.post(`admin/opportunity/assignPermissive`, { race: id, driver: driver });
    }

    public changeBranch(id: number, branch: number) {
        return this.caller.post('admin/opportunity/updateBranch', { race: id, branch: branch })
    }

    public getFiles(raceId:number,id: number): Promise<any> {
        return this.caller.get(`opportunity/${raceId}/files/${id}`);
    }

    public getScheduleById(id: number): Promise<any> {
        return this.caller.get(`opportunity/schedules/${id}`);
    }

}

class Auth {
    private caller: BaseCaller;

    constructor(caller: BaseCaller) {
        this.caller = caller;
    }


    public login(user: string, pass: string): Promise<IUser> {
        return this.caller.postForm('admin/login', {
            username: user,
            password: pass
        });
    }
}

export class Api {

    private readonly apiUrl: string;
    private readonly caller: BaseCaller;

    constructor(url: string) {
        this.apiUrl = url;
        this.caller = new BaseCaller(this.apiUrl, null)
    }

    public notifyLogin(token: string) {
        this.caller.setToken(token);
    }

    public notifyNewUrl(path: string): any {
        this.caller.setPath(path);
    }

    public notifyLogout() {
        this.caller.setToken(null);
    }

    public addErrorHandler(handler: (err: any) => (boolean)) {
        this.caller.addHandler(handler);
    }

    /**
     * Get the api to call auth functions.
     * @returns {Auth}
     */
    public auth() {
        return new Auth(this.caller);
    }


    public race() {
        return new RaceAPI(this.caller);
    }
    public visit() {
        return new VisitAPI(this.caller);
    }

    public client() {
        return new ClientApi(this.caller);
    }

    public driver() {
        return new DriverApi(this.caller);
    }

    public admin() {
        return new AdminApi(this.caller);
    }

    public fcm() {
        return new FCMApi(this.caller);
    }

    public company() {
        return new CompanyApi(this.caller);
    }

    public branch() {
        return new BranchApi(this.caller);
    }

    public report() {
        return new ReportApi(this.caller);
    }
    public history() {
        return new LocationApi(this.caller);
    }
}



// WEBPACK FOOTER //
// ./src/api/api.ts